import { useParams, Link } from 'react-router-dom';
import {
  makeStyles, tokens, Text, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { Page, PageHeader, Card, Loading, ErrorState, Empty } from '../components/common';
import { FrameworkSelector } from '../components/FrameworkSelector';
import { RunScan } from '../components/RunScan';
import { Readiness } from '../components/Readiness';
import type { RunSummary } from '../api/client';

const useStyles = makeStyles({
  tableWrap: { width: '100%', overflowX: 'auto', minWidth: 0 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  link: { color: tokens.colorBrandForeground1, textDecorationLine: 'none' },
  meta: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalL, rowGap: tokens.spacingVerticalXS },
  metaItem: { display: 'flex', flexDirection: 'column' },
});

function runStatus(run: RunSummary) {
  switch (run.status) {
    case 'complete': return { colour: 'success' as const, label: 'Complete' };
    case 'failed':   return { colour: 'danger'  as const, label: 'Failed' };
    default:         return { colour: 'warning' as const, label: 'Running' };
  }
}

function formatUtc(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString().replace('T', ' ').slice(0, 16);
}

export function TenantDetail() {
  const styles = useStyles();
  const api = useApi();
  const { tenantId } = useParams<{ tenantId: string }>();

  const tenant = useAsync(() => (tenantId ? api.getTenant(tenantId) : Promise.resolve(null)), [tenantId]);
  const runs = useAsync(() => (tenantId ? api.getRuns(tenantId) : Promise.resolve([])), [tenantId]);

  if (!tenantId) return <ErrorState error="No customer specified." title="Not found" />;
  if (tenant.loading || runs.loading) return <Loading />;
  if (tenant.error) return <ErrorState error={tenant.error} />;
  if (!tenant.data) return <ErrorState error="Customer not found on this stamp." title="Not found" />;

  const t = tenant.data;
  const history = runs.data ?? [];

  return (
    <Page>
      <PageHeader title={t.displayName} description={t.primaryDomain ?? t.entraTenantId} />

      {!t.consentGranted && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Admin consent has not been granted, or has been revoked. Nothing can be
            collected from this tenant until a Global Administrator grants it.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <span className={styles.muted}>Data region</span>
            <Text weight="semibold">{t.homeRegion}</Text>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.muted}>Consent</span>
            <Text weight="semibold">
              {t.consentGranted ? `Granted ${formatUtc(t.consentGrantedUtc)}` : 'Not granted'}
            </Text>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.muted}>Assessed against</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Text weight="semibold">
              {/* No selection is not an error. Every finding then resolves
                  through the global fallback, and the report says so. */}
              {t.frameworks.length === 0
                ? 'Global fallback only'
                : t.frameworks
                    .slice()
                    .sort((a, b) => a.priority - b.priority)
                    .map((f, i) => `${i + 1}. ${f.name}`)
                    .join('  ')}
              </Text>
              <FrameworkSelector tenant={t} onSaved={tenant.reload} />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <Readiness tenantId={t.tenantId} />
      </Card>

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Text weight="semibold" size={400}>Run an assessment</Text>
          <RunScan tenant={t} onFinished={() => { tenant.reload(); runs.reload(); }} />
        </div>
      </Card>

      <Card>
        <Text weight="semibold" size={400}>Collection history</Text>
        {history.length === 0 ? (
          <Empty>No collections yet for this customer.</Empty>
        ) : (
          <div className={styles.tableWrap}>
          <Table aria-label="Collection history" size="medium">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Started</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Findings</TableHeaderCell>
                <TableHeaderCell>Collector</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((r) => {
                const s = runStatus(r);
                return (
                  <TableRow key={r.runId}>
                    <TableCell>
                      <Link to={`/runs/${r.runId}?tenantId=${t.tenantId}`} className={styles.link}>
                        {formatUtc(r.startedUtc)}
                      </Link>
                      {r.completedUtc && (
                        <div className={styles.muted}>finished {formatUtc(r.completedUtc)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* A failed run is never shown as complete. A partial
                          collection presented as a full one is how an absent
                          finding gets read as a clean result. */}
                      <Badge appearance="tint" color={s.colour}>{s.label}</Badge>
                    </TableCell>
                    <TableCell>{r.findingCount}</TableCell>
                    <TableCell><span className={styles.muted}>{r.collectorVersion ?? ''}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>
    </Page>
  );
}
